import { useCallback } from 'react';

/**
 * useArrowNav — shared keyboard navigation hook for ZignaGenix.
 *
 * Adds arrow-key navigation to any group of focusable elements
 * (tabs, icon buttons, card grids, toolbars, etc.).
 *
 * Usage:
 *   const { onKeyDown } = useArrowNav(containerRef, selector, options);
 *   <div ref={containerRef} onKeyDown={onKeyDown}> ... </div>
 *
 * @param {React.RefObject} containerRef  - ref to the DOM container
 * @param {string}          [selector]    - CSS selector for focusable children (default: 'button, [tabindex="0"]')
 * @param {object}          [options]
 * @param {boolean}         [options.horizontal=true]  - Left/Right arrows navigate
 * @param {boolean}         [options.vertical=true]    - Up/Down arrows navigate
 * @param {boolean}         [options.wrap=true]         - wrap from last to first and vice-versa
 * @param {boolean}         [options.activateOnFocus=false] - click the element when it receives focus
 * @param {boolean}         [options.homeEnd=true]      - Home/End jump to first/last
 */
export default function useArrowNav(
  containerRef,
  selector = 'button, [tabindex="0"]',
  options = {}
) {
  const {
    horizontal = true,
    vertical = true,
    wrap = true,
    activateOnFocus = false,
    homeEnd = true,
  } = options;

  const onKeyDown = useCallback(
    (e) => {
      // ⚠ A MODIFIED ARROW IS NOT A ROVING-FOCUS KEY. NavHistory binds
      // Alt+ArrowLeft (and Ctrl/Cmd+[) on `window` as global Back
      // (src/lib/NavHistory.js:242-247). Neither handler stops propagation, so
      // without this check Alt+ArrowLeft moved focus one item left AND
      // navigated Back — and in containers using `activateOnFocus` it also
      // .click()ed the newly focused item, switching tab and leaving the page
      // from a single keystroke. NavHistory's isTyping() guard cannot help:
      // focus is on a <button>, not an input.
      //
      // Deliberately `return` rather than preventDefault(): the event must
      // reach the window-level handler intact, or this fix would silently kill
      // the Back shortcut instead of the conflict. Shift is included because
      // Shift+Arrow conventionally means range selection, never roving focus.
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

      const container = containerRef.current;
      if (!container) return;

      const items = Array.from(container.querySelectorAll(selector));
      if (items.length === 0) return;

      const currentIndex = items.indexOf(document.activeElement);

      let nextIndex = -1;

      const forward =
        (horizontal && e.key === 'ArrowRight') ||
        (vertical && e.key === 'ArrowDown');
      const backward =
        (horizontal && e.key === 'ArrowLeft') ||
        (vertical && e.key === 'ArrowUp');

      if (forward) {
        e.preventDefault();
        if (currentIndex < items.length - 1) {
          nextIndex = currentIndex + 1;
        } else if (wrap) {
          nextIndex = 0;
        }
      } else if (backward) {
        e.preventDefault();
        if (currentIndex > 0) {
          nextIndex = currentIndex - 1;
        } else if (wrap) {
          nextIndex = items.length - 1;
        }
      } else if (homeEnd && e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (homeEnd && e.key === 'End') {
        e.preventDefault();
        nextIndex = items.length - 1;
      }

      if (nextIndex >= 0 && items[nextIndex]) {
        items[nextIndex].focus();
        items[nextIndex].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        if (activateOnFocus) {
          items[nextIndex].click();
        }
      }
    },
    [containerRef, selector, horizontal, vertical, wrap, activateOnFocus, homeEnd]
  );

  return { onKeyDown };
}
